import { isEqual } from 'lodash';

/**
 * Find a PerformanceMark matching name + optional detail options.
 * Kept outside `useAppMetrics` so Vuex settings can import it without the
 * settings ↔ usePdf ↔ useAppMetrics circular module graph.
 */
export const findMatchingMark = ({
  name,
  startOrEnd,
  options,
  debug = false,
}: {
  name: string;
  startOrEnd: 'start' | 'end';
  options?: PerformanceMarkOptions;
  debug?: boolean;
}): PerformanceMark | undefined => {
  const marksMatchingName = performance.getEntriesByName(`${name}-${startOrEnd}`) as PerformanceMark[];
  if (!marksMatchingName.length) return;

  if (debug) console.log(`marks found for ${name}`, { marksMatchingName, startOrEnd, options });

  // this potentially is fed options with an error detail, which we must remove for the comparison
  const potentiallyErrorMarkDetail = { ...options?.detail };
  if (!!potentiallyErrorMarkDetail && 'error' in potentiallyErrorMarkDetail) {
    delete potentiallyErrorMarkDetail.error;
  }
  // find if a matching mark exists without the error detail
  const marksMatchingOptions = marksMatchingName.filter((mark) => {
    return isEqual(potentiallyErrorMarkDetail, mark.detail);
  });
  if (debug) console.log({ matchingMark: marksMatchingOptions });

  // if there is only one mark matching the options, return it
  if (marksMatchingOptions.length === 1) {
    return marksMatchingOptions[0];
  }

  if (debug) console.log(`no mark was found matching name ${name} and options:`, options);

  if (!options) {
    if (debug) console.log('but no options passed, so we go back to just name matches');

    // multiple marks found, so we don't know which one to return
    if (marksMatchingName.length > 1) {
      if (debug) console.log('multiple marks found!!');

      return;
    }

    // otherwise only one mark found, so we return it
    return marksMatchingName[0];
  }
};
